/**
 * B177 — the app theme, spoken in Stripe's language.
 *
 * The Payment Element renders inside Stripe's own iframe: our stylesheet
 * cannot reach it and contrast.test.ts cannot see it. Left alone Stripe
 * half-adapts, which in dark mode meant dark-on-dark field labels on the
 * one screen where a buyer is handing over a card. The only lever is the
 * Elements `appearance` object, so this module derives it from the SAME
 * --transact-* tokens every other surface reads.
 *
 * Two sources, in order: the live resolved values off the document root
 * (so a channel's brand override and the host page's §9.2 hints reach
 * Stripe too), and — when nothing resolves, as in SSR, a detached root,
 * or a browser that hands back "" — a per-theme table mirroring
 * tokens.css. The table is drift-walled against tokens.css in
 * test/paymentAppearance.test.ts; the fallback exists so the failure
 * mode is "the tokens, one build stale" rather than "Stripe's light
 * default over a dark page", which is the bug itself.
 */
import type { TransactTheme } from "@transact/ui";

/** The tokens the appearance consumes — the drift wall iterates this. */
export const APPEARANCE_TOKENS = [
  "--transact-accent",
  "--transact-surface",
  "--transact-text",
  "--transact-text-soft",
  "--transact-muted",
  "--transact-border",
  "--transact-danger",
  "--transact-success",
  "--transact-warning",
  "--transact-font",
  "--transact-font-size",
  "--transact-space",
  "--transact-radius-small",
  "--transact-focus-ring",
] as const;

export type AppearanceToken = (typeof APPEARANCE_TOKENS)[number];

/**
 * tokens.css, mirrored per theme — dark carries the value the browser
 * would RESOLVE there (the dark block's override, else the :root value),
 * which is why shape and type repeat rather than being shared.
 */
export const APPEARANCE_TOKEN_FALLBACK: Readonly<Record<TransactTheme, Readonly<Record<AppearanceToken, string>>>> = {
  light: {
    "--transact-accent": "#3d7fe8",
    "--transact-surface": "#ffffff",
    "--transact-text": "#1c2a4a",
    "--transact-text-soft": "#46536f",
    "--transact-muted": "#3f4b63",
    "--transact-border": "#e6e9f0",
    "--transact-danger": "#e5484d",
    "--transact-success": "#1f9d61",
    "--transact-warning": "#d97706",
    "--transact-font": '"Source Sans 3", "Segoe UI", "Helvetica Neue", ui-sans-serif, system-ui, sans-serif',
    "--transact-font-size": "15px",
    "--transact-space": "4px",
    "--transact-radius-small": "8px",
    "--transact-focus-ring": "0 0 0 3px rgba(61, 127, 232, 0.4)",
  },
  dark: {
    "--transact-accent": "#568de9",
    "--transact-surface": "#222630",
    "--transact-text": "#ffffff",
    "--transact-text-soft": "#c3ccdd",
    "--transact-muted": "#a89fb8",
    "--transact-border": "#3d4557",
    "--transact-danger": "#e5484d",
    "--transact-success": "#2fd07e",
    "--transact-warning": "#ffb224",
    "--transact-font": '"Source Sans 3", "Segoe UI", "Helvetica Neue", ui-sans-serif, system-ui, sans-serif',
    "--transact-font-size": "15px",
    "--transact-space": "4px",
    "--transact-radius-small": "8px",
    "--transact-focus-ring": "0 0 0 3px rgba(91, 155, 255, 0.45)",
  },
};

/** Stripe's appearance object, narrowed to the slice we actually set. */
export interface PaymentAppearance {
  /** Stripe's base theme — unmapped chrome inherits from the right side. */
  readonly theme: "stripe" | "night";
  readonly variables: Readonly<Record<string, string>>;
  readonly rules: Readonly<Record<string, Readonly<Record<string, string>>>>;
}

export type TokenReader = (token: AppearanceToken) => string | undefined;

/** A resolved value only counts if it is non-blank — "" means unset. */
function nonBlank(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

export interface ComputedStyleLike {
  getPropertyValue(name: string): string;
}

/** getComputedStyle hands back leading whitespace and "" for unset vars. */
export function computedTokenReader(style: ComputedStyleLike): TokenReader {
  return (token) => nonBlank(style.getPropertyValue(token));
}

export interface ThemedRootLike {
  getAttribute(name: string): string | null;
}

/** The theme comes from the ONE attribute tokens.css keys the dark set off. */
export function appearanceThemeFromRoot(root: ThemedRootLike): TransactTheme {
  return root.getAttribute("data-transact-theme") === "dark" ? "dark" : "light";
}

/**
 * The derivation. Every variable is a token — nothing here is a literal
 * colour, so re-branding the product re-brands the card form with it.
 */
export function paymentElementAppearance(input: {
  readonly theme: TransactTheme;
  readonly read?: TokenReader;
}): PaymentAppearance {
  const table = APPEARANCE_TOKEN_FALLBACK[input.theme];
  const token = (name: AppearanceToken): string => nonBlank(input.read?.(name)) ?? table[name];

  const text = token("--transact-text");
  const border = token("--transact-border");

  return {
    theme: input.theme === "dark" ? "night" : "stripe",
    variables: {
      colorPrimary: token("--transact-accent"),
      colorBackground: token("--transact-surface"),
      colorText: text,
      colorTextSecondary: token("--transact-text-soft"),
      colorTextPlaceholder: token("--transact-muted"),
      colorIcon: token("--transact-text-soft"),
      colorDanger: token("--transact-danger"),
      colorSuccess: token("--transact-success"),
      colorWarning: token("--transact-warning"),
      fontFamily: token("--transact-font"),
      fontSizeBase: token("--transact-font-size"),
      spacingUnit: token("--transact-space"),
      borderRadius: token("--transact-radius-small"),
    },
    rules: {
      /* The B177 sighting itself: Stripe's own label colour, pinned. */
      ".Label": { color: text },
      ".Input": { backgroundColor: token("--transact-surface"), color: text, border: `1px solid ${border}` },
      ".Input:focus": { boxShadow: token("--transact-focus-ring"), borderColor: token("--transact-accent") },
      ".Input::placeholder": { color: token("--transact-muted") },
      ".Error": { color: token("--transact-danger") },
      ".Tab": { backgroundColor: token("--transact-surface"), color: text, border: `1px solid ${border}` },
      ".Tab--selected": { borderColor: token("--transact-accent"), color: text },
      ".TabLabel": { color: text },
      ".TabIcon": { color: token("--transact-text-soft") },
    },
  };
}

/* --------------------------------------------------------- browser wiring */

/** The appearance for the document as it is themed right now. */
export function documentPaymentAppearance(doc: Document = document): PaymentAppearance {
  const root = doc.documentElement;
  const view = doc.defaultView;
  const theme = appearanceThemeFromRoot(root);
  /* No view (SSR, detached document) → the table is the whole answer. */
  if (view === null) return paymentElementAppearance({ theme });
  return paymentElementAppearance({ theme, read: computedTokenReader(view.getComputedStyle(root)) });
}

/**
 * The hosted storefront ships a ThemeToggle, so the buyer can flip the
 * theme with the element already mounted. Watching the one attribute
 * re-derives and re-applies rather than leaving a light form on a dark
 * page — the same bug, one toggle later.
 */
export function observePaymentAppearance(
  onChange: (appearance: PaymentAppearance) => void,
  doc: Document = document,
): () => void {
  const view = doc.defaultView;
  if (view === null || typeof view.MutationObserver !== "function") return () => undefined;
  const observer = new view.MutationObserver(() => onChange(documentPaymentAppearance(doc)));
  observer.observe(doc.documentElement, { attributes: true, attributeFilter: ["data-transact-theme", "style"] });
  return () => observer.disconnect();
}
